import { HttpErrorResponse, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Store } from '@ngrx/store';
import { BrowserStorageService } from '@elite/shared/util-browser-storage';
import { formatErrorMessageAsText } from '@elite/shared/util';
import { toast } from '@spartan-ng/brain/sonner';
import { catchError, filter, of, retry, tap, timer, EMPTY } from 'rxjs';
import { logout } from '@elite/authentication/domain';

export interface ResultResponse {
  readonly success: boolean;
  readonly statusCode: number;
  readonly errorCode?: string | null;
  readonly errors?: Array<string> | null;
  result?: unknown;
}

/** Extra attempts after the first, so a request is issued at most 1 + this many times. */
const RETRY_ATTEMPTS = 3;
/** Backoff base; delays are 400ms, 800ms, 1600ms. */
const RETRY_BASE_DELAY_MS = 400;

/**
 * Whether a failed request is safe to reissue automatically.
 *
 * Two conditions, and both matter:
 *
 * 1. It must be a *read*. This API is CQRS-over-POST — of 468 generated
 *    endpoints, 160 are `/api/{Resource}/Query/...` (reads) and 308 are
 *    `/Command/...` (writes), and nearly all are POST. So the HTTP verb tells
 *    us nothing; the path does. Replaying a Command could create duplicate
 *    records, which is far worse than surfacing an error.
 * 2. The failure must be transient. Status 0 is a network/offline/CORS
 *    failure and 5xx is a server-side fault, both of which can succeed on a
 *    second try. A 4xx will not fix itself — 401, 403, 400 and 404 are
 *    decisions, not blips — and retrying them just delays the message.
 */
function isRetryable(request: HttpRequest<unknown>, error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) return false;

  const isRead = request.method === 'GET' || /\/query\//i.test(request.url);
  if (!isRead) return false;

  return error.status === 0 || error.status >= 500;
}

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const browserStorage = inject(BrowserStorageService);
  const platformId = inject(PLATFORM_ID);
  const store = inject(Store);
  const isBrowser = isPlatformBrowser(platformId);

  // Skip auth interceptor for the token endpoint and Google Maps
  if (request.url.endsWith('CommandToken') || request.url.includes('googleapis')) {
    return next(request);
  }

  const token = browserStorage.get('token');

  const showErrorToast = (statusCode: number, errors: Array<string> | null, url = '') => {
    // Only show toasts in browser, not during SSR
    if (!isBrowser) {
      return;
    }

    let errorMessage: string;

    switch (statusCode) {
      case 400:
        errorMessage = 'Bad Request';
        break;
      case 401:
        errorMessage = 'Unauthorized - Please login again';
        // Dispatch logout action to properly clean up authentication state
        store.dispatch(logout());
        break;
      case 403: {
        // Extract resource/action from API URL structure for more specific error message
        let resourceHint = '';
        let actionHint = '';

        // Parse API path structure: /api/{Resource}/{Action?}/{Specific?}
        const apiMatch = url.match(/\/api\/([^/]+)/i);
        if (apiMatch && apiMatch[1]) {
          const resourceName = apiMatch[1].toLowerCase();
          // Convert PascalCase API resource names to readable format
          const readableResource = resourceName
            .replace(/([A-Z])/g, ' $1')
            .toLowerCase()
            .trim();
          resourceHint = readableResource;
        }

        // Extract action from URL path (API pattern: /api/{Resource}/{Query|Command}/{Specific})
        const urlLower = url.toLowerCase();
        if (urlLower.includes('/query/')) {
          actionHint = 'view ';
        } else if (urlLower.includes('/command/')) {
          actionHint = 'modify ';
        } else {
          actionHint = 'access ';
        }

        if (resourceHint) {
          errorMessage = `Access denied. You don't have permission to ${actionHint}${resourceHint}. Please contact your administrator for the necessary permissions.`;
        } else {
          errorMessage = `Access denied. You don't have permission to perform this action. Please contact your administrator for the necessary permissions.`;
        }

        break;
      }
      case 404:
        errorMessage = 'Resource not found';
        break;
      case 500:
        errorMessage = 'Internal Server Error';
        break;
      default:
        errorMessage = 'An error occurred';
    }

    // No request URL in the text: the store that made the call reports the same `errors` without
    // one, and only identical text is collapsed into a single toast (provideProblemToastLifecycle).
    const description = statusCode === 403 ? errorMessage : (formatErrorMessageAsText(errors) ?? errorMessage);

    // Sticky + dismissible, matching the old sticky/closable toast. sonner
    // prints `description` as text, so it gets the plain-text formatter.
    toast.error('Error', { description, duration: Infinity, closeButton: true });
  };

  if (token) {
    const authReq = request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`),
    });

    return next(authReq).pipe(
      // Retry transient read failures before anything downstream sees them, so
      // a brief network blip never reaches the user. Writes are never replayed
      // — see isRetryable(). Must sit above catchError, which converts errors
      // into a synthetic 200 and would otherwise swallow them first.
      retry({
        count: RETRY_ATTEMPTS,
        delay: (error, retryCount) => {
          if (!isRetryable(authReq, error)) throw error;
          return timer(RETRY_BASE_DELAY_MS * 2 ** (retryCount - 1));
        },
      }),
      filter((event) => event instanceof HttpResponse),

      tap((event) => {
        if (event instanceof HttpResponse && event.url?.includes('/api/')) {
          const body = event.body as ResultResponse;
          if (!body.success && body.errorCode !== 'CoreErrors.UnAuthorizedError') {
            showErrorToast(body.statusCode, body.errors ?? ['An error occurred'], event.url ?? '');
          }
        }
      }),
      catchError((_error: HttpErrorResponse) => {
        // During SSR, suppress errors to prevent server crash
        if (!isBrowser) {
          console.warn('[SSR] HTTP error suppressed during server-side rendering');
          return EMPTY;
        }

        // Handle network/client-side errors. For reads this is only reached
        // after the retries above have all failed.
        const errorResponse: ResultResponse = {
          success: false,
          statusCode: 500, // Assume server error for network issues
          errorCode: 'NETWORK_ERROR',
          errors: ['Unable to connect to the server. Please check your connection.'],
        };

        showErrorToast(500, errorResponse.errors ?? ['An error occurred']);

        return of(
          new HttpResponse({
            body: errorResponse,
            status: 200,
          }),
        );
      }),
    );
  } else {
    return next(request);
  }
};
